// Catalog.ID Client — Device Label Encryption
// ============================================================================
// A device's human name ("evk-laptop (Linux)") is a hostname, and hostnames
// are exactly the kind of ambient metadata the server has no business holding.
// So the label is self-encrypted in the browser and the server stores an
// opaque string it can only length-check.
//
// Recipe — the claim self-encryption pattern (crypto/claims.ts), serialised as
// ONE JSON string so it fits a single `label` column:
//
//   { "v":1, "ct": base64, "n": base64, "ek": base64 }
//
//   ct — the label under AES-256-GCM with a fresh random key and nonce
//   n  — that nonce
//   ek — the AES key wrapped to the account's attr_encrypt hybrid public key
//        (X25519 + ML-KEM-1024), i.e. kemEncapsulate output
//
// The `labelKeyIndex` stored alongside names the attr_encrypt key index used.
// Key rotation keeps every prior secret key in the chain
// (state/keys.ts getKeyChain), so an old label decrypts with the key its index
// names and there is no rewrap step anywhere — a label written before a
// rotation stays readable after it.

import * as aes from './aes';
import { wrapSymmetricKey, unwrapSymmetricKey } from './claims';

/** Envelope format version. Bump only on a breaking shape change. */
export const DEVICE_LABEL_ENVELOPE_VERSION = 1;

/** Longest plaintext label the UI accepts (device-name input maxlength). */
export const MAX_DEVICE_LABEL_CHARS = 100;

/** Server-side cap on the serialised envelope. A 100-char label lands
 *  around 2.5 KB (the 1693-byte KEM blob dominates), so this is slack. */
export const MAX_DEVICE_LABEL_ENVELOPE_CHARS = 8192;

interface DeviceLabelEnvelope {
    v: number;
    /** base64 AES-256-GCM ciphertext of the UTF-8 label. */
    ct: string;
    /** base64 AES-GCM nonce. */
    n: string;
    /** base64 hybrid-KEM-wrapped AES key. */
    ek: string;
}

/**
 * Encrypt a device label to an account's attr_encrypt hybrid PUBLIC key.
 * Returns the serialised envelope — the value of the wire `label` field.
 */
export async function encryptDeviceLabel(label: string, attrPublicKey: Uint8Array): Promise<string> {
    const symKey = aes.generateSymmetricKey();
    const nonce = aes.generateNonce();
    const ciphertext = await aes.encrypt(symKey, nonce, aes.encodeUtf8(label));
    const wrapped = await wrapSymmetricKey(symKey, attrPublicKey);

    const envelope: DeviceLabelEnvelope = {
        v: DEVICE_LABEL_ENVELOPE_VERSION,
        ct: aes.toBase64(ciphertext),
        n: aes.toBase64(nonce),
        ek: aes.toBase64(wrapped),
    };
    return JSON.stringify(envelope);
}

/**
 * Decrypt a device label with the attr_encrypt SECRET key whose keyIndex
 * matches the record's `labelKeyIndex`.
 *
 * Throws on a malformed envelope, an unknown version, or a key that cannot
 * open it. Callers render a fallback title rather than surfacing the error.
 */
export async function decryptDeviceLabel(envelope: string, secretKey: Uint8Array): Promise<string> {
    let parsed: Partial<DeviceLabelEnvelope>;
    try {
        parsed = JSON.parse(envelope) as Partial<DeviceLabelEnvelope>;
    } catch {
        throw new Error('Device label envelope is malformed (not JSON).');
    }
    if (parsed === null || typeof parsed !== 'object') {
        throw new Error('Device label envelope is malformed (not an object).');
    }
    if (parsed.v !== DEVICE_LABEL_ENVELOPE_VERSION) {
        throw new Error(`Device label envelope: unsupported version ${String(parsed.v)}.`);
    }
    if (typeof parsed.ct !== 'string' || typeof parsed.n !== 'string' || typeof parsed.ek !== 'string') {
        throw new Error('Device label envelope is malformed (missing ct/n/ek).');
    }

    const symKey = await unwrapSymmetricKey(aes.fromBase64(parsed.ek), secretKey);
    const plaintext = await aes.decrypt(symKey, aes.fromBase64(parsed.n), aes.fromBase64(parsed.ct));
    return aes.decodeUtf8(plaintext);
}

// ============================================================================
// attr_encrypt key selection
// ============================================================================

/** The subset of `PublicKeyInfo` this module needs. Structural so callers can
 *  pass `PublicKeyInfo[]` straight from `accountApi.getPublicKeys()` and get
 *  the same element type back. */
export interface PublicKeyCandidate {
    keyId: string;
    publicKey: string;
    keyIndex: number;
    status?: string;
}

/**
 * Pick the attr_encrypt public key to encrypt a NEW label with: active (the
 * publickeys endpoint returns only active rows and omits the field entirely,
 * so `undefined` counts as active) and the highest keyIndex.
 * Returns null when the account has no usable attr_encrypt key.
 */
export function selectAttrEncryptKey<T extends PublicKeyCandidate>(keys: T[]): T | null {
    let best: T | null = null;
    for (const k of keys) {
        if (k.keyId !== 'attr_encrypt') continue;
        if (k.status !== undefined && k.status !== 'active') continue;
        if (best === null || k.keyIndex > best.keyIndex) best = k;
    }
    return best;
}

// ============================================================================
// User-agent description (the standalone-mode default label)
// ============================================================================

// Order matters. Edge and Opera user agents both contain "Chrome/", and every
// Chromium UA ends in "Safari/", so the more specific token has to win first.
const BROWSERS: [RegExp, string][] = [
    [/Edg(?:e|A|iOS)?\//, 'Edge'],
    [/(?:OPR|OPiOS)\/|\bOpera[\s/]/, 'Opera'],
    [/(?:Firefox|FxiOS)\//, 'Firefox'],
    [/(?:Chrome|Chromium|CriOS)\//, 'Chrome'],
    [/Safari\//, 'Safari'],
];

// Order matters here too: Android UAs say "Linux", and iOS UAs say
// "like Mac OS X".
const PLATFORMS: [RegExp, string][] = [
    [/Android/, 'Android'],
    [/iPhone|iPad|iPod|\biOS\b/, 'iOS'],
    [/Windows/, 'Windows'],
    [/CrOS/, 'ChromeOS'],
    [/Macintosh|Mac OS X/, 'macOS'],
    [/Linux|X11/, 'Linux'],
];

function firstMatch(ua: string, table: [RegExp, string][]): string | null {
    for (const [pattern, name] of table) if (pattern.test(ua)) return name;
    return null;
}

/**
 * Human description of a browser from its user-agent string, e.g.
 * "Firefox on Linux". Used to prefill the device-name field when the desktop
 * helper does not supply a real machine name (standalone site, older helper).
 * Falls back to the browser alone, then to "Browser" — never to an empty
 * string, because an empty prefill invites an unnamed device.
 */
export function describeUserAgent(ua: string): string {
    const browser = firstMatch(ua, BROWSERS);
    if (!browser) return 'Browser';
    const platform = firstMatch(ua, PLATFORMS);
    return platform ? `${browser} on ${platform}` : browser;
}
